import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { css } from 'styled-system/css';
import { LogoMark } from '../ui/logo/LogoMark';
import { accessCtaClass } from './AccessCta';

const navLinks = [
  { name: 'Pricing', href: '/pricing' },
  { name: 'About', href: '/about' },
];

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    function handleScroll() {
      setIsScrolled(window.scrollY > 0);
    }
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    }
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [isMobileMenuOpen]);

  function toggleMobileMenu() {
    setIsMobileMenuOpen((open) => !open);
  }

  function closeMobileMenu() {
    setIsMobileMenuOpen(false);
  }

  return (
    <header
      className={css({
        position: 'fixed',
        zIndex: 50,
        top: 0,
        left: 0,
        right: 0,
        paddingTop: { base: '4', md: '5' },
        paddingInline: { base: '4', md: '6' },
      })}
    >
      <nav
        aria-label="Primary"
        data-scrolled={isScrolled || undefined}
        className={css({
          maxWidth: 'content',
          marginInline: 'auto',
          paddingInline: { base: '5', md: '8' },
          height: '16',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8',
          border: '1px solid',
          borderColor: 'transparent',
          borderRadius: '1px',
          background: 'transparent',
          transition:
            'background 320ms {easings.out}, border-color 320ms {easings.out}, backdrop-filter 320ms {easings.out}',
          '&[data-scrolled]': {
            borderColor: 'border.hairline',
            background: 'bg.page/80',
            backdropFilter: 'blur(16px)',
          },
        })}
      >
        <a
          href="/"
          aria-label="Exis — home"
          onClick={closeMobileMenu}
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '2.5',
            color: 'text.primary',
            _focusVisible: {
              outline: '2px solid token(colors.border.focus)',
              outlineOffset: '2px',
            },
          })}
        >
          <LogoMark size={22} prism />
          <span
            className={css({
              fontFamily: 'display',
              fontWeight: 'bold',
              fontSize: 'lg',
              letterSpacing: 'tight',
              lineHeight: '1',
            })}
          >
            Exis
          </span>
        </a>

        <div
          className={css({
            display: { base: 'none', md: 'flex' },
            alignItems: 'center',
            gap: '8',
            marginLeft: 'auto',
          })}
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={css({
                fontSize: 'sm',
                color: 'text.muted',
                transition: 'color 200ms {easings.out}',
                _hover: { color: 'text.primary' },
                _focusVisible: {
                  outline: '2px solid token(colors.border.focus)',
                  outlineOffset: '2px',
                },
              })}
            >
              {link.name}
            </a>
          ))}
          <a href="/#get-access" className={accessCtaClass('md')}>
            Join waitlist
          </a>
        </div>

        <button
          type="button"
          onClick={toggleMobileMenu}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
          className={css({
            display: { base: 'inline-flex', md: 'none' },
            padding: '3',
            color: 'text.primary',
            cursor: 'pointer',
            _focusVisible: {
              outline: '2px solid token(colors.border.focus)',
              outlineOffset: '2px',
            },
          })}
        >
          {isMobileMenuOpen ? (
            <X className={css({ width: '5', height: '5' })} />
          ) : (
            <Menu className={css({ width: '5', height: '5' })} />
          )}
        </button>
      </nav>

      <div
        id="mobile-menu"
        data-open={isMobileMenuOpen || undefined}
        className={css({
          display: { md: 'none' },
          position: 'fixed',
          top: { base: '20', md: 'calc(4rem + 1.25rem)' },
          left: 0,
          right: 0,
          background: 'bg.page',
          borderBottom: '1px solid',
          borderColor: 'border.hairline',
          opacity: 0,
          visibility: 'hidden',
          transform: 'translateY(-8px)',
          pointerEvents: 'none',
          transition:
            'opacity 320ms {easings.out}, transform 320ms {easings.out}',
          '&[data-open]': {
            opacity: 1,
            visibility: 'visible',
            transform: 'translateY(0)',
            pointerEvents: 'auto',
          },
        })}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '1',
            padding: '5',
          })}
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={closeMobileMenu}
              className={css({
                fontSize: 'sm',
                color: 'text.primary',
                paddingBlock: '3',
              })}
            >
              {link.name}
            </a>
          ))}
          <a
            href="/#get-access"
            onClick={closeMobileMenu}
            className={accessCtaClass('md')}
          >
            Join waitlist
          </a>
        </div>
      </div>
    </header>
  );
}
